// src/pages/admin/tabs/parts/PostsTab.jsx
import React from 'react';
import { AdminAPI } from '../../../../api/admin';
import {
  Card, Grid, Badge, Paginator, PageBtn,
  Actions, Btn, Field, Row2, TableWrap, Table
} from '../../ui';
import PostForm from './PostForm';

const DEFAULT_QUERY = {
  page: 1, page_size: 20,
  section: 'blog',
  is_published: '',
  is_active: '',
  tag: '',
  q: '',
};

function yesNo(v){
  if (v === true) return 'Да';
  if (v === false) return 'Нет';
  return '—';
}
function fmt(dt){
  if (!dt) return '—';
  try {
    const d = new Date(dt);
    const pad = (n)=>String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return dt; }
}

export default function PostsTab(){
  const [query, setQuery] = React.useState(DEFAULT_QUERY);
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [err, setErr] = React.useState('');
  const [editing, setEditing] = React.useState(null); // объект поста или null
  const [showForm, setShowForm] = React.useState(false); // режим отдельной менюшки
  const totalPages = Math.max(1, Math.ceil((total || items.length) / (query.page_size || 20)));

  const load = React.useCallback(async ()=>{
    setLoading(true); setErr(''); setMsg('');
    try{
      const data = await AdminAPI.listPosts({
        page: query.page,
        page_size: query.page_size,
        section: query.section || undefined,
        is_published: query.is_published || undefined,
        is_active: query.is_active || undefined,
        tag: query.tag || undefined,
        q: query.q || undefined,
      });
      // сервер отдает {"posts":[...], "pagination": {...}}
      const list = Array.isArray(data?.posts) ? data.posts : (Array.isArray(data) ? data : []);
      const total = data?.pagination?.total ?? list.length;
      setItems(list);
      setTotal(total);
    } catch (e){
      setErr(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [query]);

  React.useEffect(()=>{ load(); }, [load]);

  const goPage = (n)=>{
    const clamped = Math.max(1, Math.min(n, totalPages));
    setQuery(q => ({ ...q, page: clamped }));
  };

  const remove = async (p)=>{
    if (!window.confirm(`Удалить пост “${p.title_ru || p.title_en || p.slug}”?`)) return;
    try {
      await AdminAPI.deletePost(p.id);
      setMsg('Пост удален');
      await load();
    } catch(e){
      setErr(e.message || 'Ошибка удаления');
    }
  };

  const togglePublish = async (p, publish)=>{
    try {
      await AdminAPI.publishPost(p.id, publish);
      setMsg(publish ? 'Опубликовано' : 'Снято с публикации');
      await load();
    } catch(e){
      setErr(e.message || 'Ошибка изменения статуса');
    }
  };

  // === ОТДЕЛЬНАЯ МЕНЮШКА СОЗДАНИЯ/РЕДАКТИРОВАНИЯ ===
  if (showForm || editing) {
    return (
      <>
        <Actions style={{marginBottom:12}}>
          <Btn onClick={()=>{
            setEditing(null);
            setShowForm(false);
          }}>
            ← К списку постов
          </Btn>
        </Actions>

        <Card>
          <h3>{editing ? 'Редактировать пост' : 'Создать пост'}</h3>
          {msg && <div style={{color:'#666', marginBottom:8}}>{msg}</div>}
          {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}

          <PostForm
            editing={editing}
            onSaved={async (savedSection)=>{
              const wasEdit = !!editing;
              setEditing(null);
              setShowForm(false); // вернуться к списку
              
              // Автоматически переключаемся на секцию сохраненного поста
              if (savedSection && savedSection !== query.section) {
                setQuery(q => ({ ...q, section: savedSection, page: 1 }));
              } else {
                await load();
              }
              
              setMsg(wasEdit ? 'Изменения сохранены' : 'Пост создан');
            }}
            onCancel={()=>{
              setEditing(null);
              setShowForm(false); // вернуться к списку
            }}
          />
        </Card>
      </>
    );
  }

  // === ОБЫЧНЫЙ РЕЖИМ: СПИСОК + ФИЛЬТРЫ ===
  return (
    <>
      {/* Верхнее меню — кнопка открывает отдельную менюшку */}
      <Actions style={{marginBottom:12}}>
        <Btn
          $kind="primary"
          onClick={()=>{
            setEditing(null);     // создание нового
            setShowForm(true);    // открыть отдельную менюшку
          }}
        >
          + Создать пост
        </Btn>
      </Actions>

      <Grid>
        <Card>
          <h3>Фильтры и поиск</h3>
          {msg && <div style={{color:'#666', marginBottom:8}}>{msg}</div>}
          {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}

          <Row2>
            <Field>Секция
              <select value={query.section} onChange={e=>setQuery(q=>({...q, section:e.target.value, page:1}))}>
                <option value="blog">blog</option>
                <option value="news">news</option>
                <option value="media">media</option>
                <option value="about">about</option>
              </select>
            </Field>
            <Field>Опубликован
              <select value={query.is_published} onChange={e=>setQuery(q=>({...q, is_published:e.target.value, page:1}))}>
                <option value="">все</option>
                <option value="true">да</option>
                <option value="false">нет</option>
              </select>
            </Field>
            <Field>Активен
              <select value={query.is_active} onChange={e=>setQuery(q=>({...q, is_active:e.target.value, page:1}))}>
                <option value="">все</option>
                <option value="true">да</option>
                <option value="false">нет</option>
              </select>
            </Field>
            <Field>Тег
              <input value={query.tag} onChange={e=>setQuery(q=>({...q, tag:e.target.value, page:1}))} placeholder="напр. media"/>
            </Field>
            <Field>Поиск
              <input value={query.q} onChange={e=>setQuery(q=>({...q, q:e.target.value, page:1}))} placeholder="в заголовке/анонсе"/>
            </Field>
          </Row2>

          <Actions style={{marginTop:10}}>
            <Btn onClick={()=>setQuery(DEFAULT_QUERY)}>Сброс</Btn>
            <Btn onClick={load}>Обновить</Btn>
            <Badge>Найдено: <b>{total}</b></Badge>
          </Actions>
        </Card>
      </Grid>

      <Card style={{marginTop:12}}>
        <h3>Список постов {loading ? '(загрузка...)' : ''}</h3>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Секция</th>
                <th>Slug</th>
                <th>Заголовок RU / EN</th>
                <th>Опубл.</th>
                <th>Активен</th>
                <th>Избран.</th>
                <th>Дата публикации</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(p=>(
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.section}</td>
                  <td>{p.slug}</td>
                  <td style={{maxWidth:360}}>
                    <div style={{fontWeight:600}}>{p.title_ru}</div>
                    <div style={{opacity:.75}}>{p.title_en}</div>
                  </td>
                  <td>{yesNo(p.is_published)}</td>
                  <td>{yesNo(p.is_active)}</td>
                  <td>{yesNo(p.is_featured)}</td>
                  <td>{fmt(p.published_at)}</td>
                  <td style={{display:'flex', gap:8}}>
                    <Btn onClick={()=>{
                      setEditing(p);
                      setShowForm(true);   // открыть отдельную менюшку редактирования
                    }}>Редакт.</Btn>
                    {p.is_published
                      ? <Btn onClick={()=>togglePublish(p,false)}>Снять</Btn>
                      : <Btn onClick={()=>togglePublish(p,true)} data-primary="true">Опубликовать</Btn>}
                    <Btn onClick={()=>remove(p)}>Удалить</Btn>
                  </td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr><td colSpan={9} style={{opacity:.7}}>Постов нет</td></tr>
              )}
            </tbody>
          </Table>
        </TableWrap>

        <Paginator>
          <PageBtn onClick={()=>goPage(1)} disabled={query.page<=1}>« Первая</PageBtn>
          <PageBtn onClick={()=>goPage(query.page-1)} disabled={query.page<=1}>‹ Назад</PageBtn>
          <PageBtn data-active="true">{query.page} / {totalPages}</PageBtn>
          <PageBtn onClick={()=>goPage(query.page+1)} disabled={query.page>=totalPages}>Вперёд ›</PageBtn>
          <PageBtn onClick={()=>goPage(totalPages)} disabled={query.page>=totalPages}>Последняя »</PageBtn>
        </Paginator>
      </Card>
    </>
  );
}
